import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// UI
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
// Context
import { useAuth } from 'AuthContext';
import useFeedContext from 'contexts/Feed';
// Components
import IOSConfirmDialog from 'components/IOSConfirmDialog';
import LegalLinks from 'components/LegalLinks';
import ProfileAvatar from 'components/ProfileAvatar';
// Services
import { deleteAccount } from 'services/users/users.service';
import { getBlockedUsers } from 'services/moderation/moderation.service';

const AccountSettings = () => {
  const { user, logout, revokeAppleAccessIfNeeded } = useAuth();
  const { unblockUser: unblockUserFromFeed } = useFeedContext();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [unblockingId, setUnblockingId] = useState(null);

  const loadBlocks = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setBlocksLoading(true);
      const data = await getBlockedUsers(user.uid);
      setBlocks(data.blocks || []);
    } catch (err) {
      console.error('Error loading blocked users:', err);
    } finally {
      setBlocksLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  const handleUnblock = async (blockedUserId) => {
    if (!user?.uid || !blockedUserId) return;
    try {
      setUnblockingId(blockedUserId);
      if (typeof unblockUserFromFeed === 'function') {
        await unblockUserFromFeed(blockedUserId);
      }
      setBlocks((prev) => prev.filter((b) => b.blockedUserId !== blockedUserId));
    } catch (err) {
      console.error('Error unblocking user:', err);
      setError(err.message || 'Failed to unblock user');
    } finally {
      setUnblockingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!user) return;

    try {
      setDeleting(true);
      setError(null);
      // Apple-linked accounts must have their token revoked before deletion.
      // This prompts a fresh Apple sign-in; abort deletion if it doesn't complete
      // so we never delete the account while leaving the Apple token valid.
      try {
        await revokeAppleAccessIfNeeded();
      } catch (revokeErr) {
        console.error('Error revoking Apple access:', revokeErr);
        setError(
          'To delete your account, please complete the Apple re-authentication. Please try again.'
        );
        setDeleteDialogOpen(false);
        return;
      }
      await deleteAccount(user.uid);
      setDeleteDialogOpen(false);
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message || 'Failed to delete account');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Sign in to manage your account.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <LegalLinks variant="settings" />

      <Box>
        <Typography variant="h6">Blocked users</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
          Blocked members stay in your club, but their posts are hidden from your feed.
        </Typography>
        {blocksLoading ? (
          <CircularProgress size={24} />
        ) : blocks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You have not blocked anyone.
          </Typography>
        ) : (
          <List dense disablePadding>
            {blocks.map((block) => (
              <ListItem
                key={block.id || block.blockedUserId}
                secondaryAction={
                  <Button
                    size="small"
                    onClick={() => handleUnblock(block.blockedUserId)}
                    disabled={unblockingId === block.blockedUserId}
                    sx={{ textTransform: 'none' }}
                  >
                    {unblockingId === block.blockedUserId ? 'Unblocking…' : 'Unblock'}
                  </Button>
                }
              >
                <ListItemAvatar>
                  <ProfileAvatar
                    user={block.blockedUser || { uid: block.blockedUserId }}
                    size={36}
                    disableGoalModal
                    showEntryRing={false}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={block.blockedUser?.displayName || 'Member'}
                  secondary="Hidden from your feed"
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Box>
        <Typography variant="h6" sx={{ color: 'error.main' }}>
          Danger zone
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Permanently delete your account and all associated data, including goals, posts,
          and club memberships. This action cannot be undone.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={deleting}
        >
          Delete account
        </Button>
      </Box>

      <IOSConfirmDialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        title="Delete your account?"
        description="This will permanently delete your account, goals, posts, and club memberships. If you are the only owner of a club, transfer ownership or delete that club first. This action cannot be undone."
        confirmLabel="Delete account"
        cancelLabel="Cancel"
        confirmDisabled={deleting}
        destructive
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default AccountSettings;
